import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { Icon } from './Icon.js';

export interface ModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title?: ReactNode;
  readonly children?: ReactNode;
  readonly footer?: ReactNode;
  readonly width?: number;
  readonly closeOnScrimClick?: boolean;
  readonly closeOnEsc?: boolean;
  readonly testId?: string;
  readonly labelledBy?: string;
}

let modalIdCounter = 0;

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  width,
  closeOnScrimClick = true,
  closeOnEsc = true,
  testId,
  labelledBy,
}: ModalProps): JSX.Element | null {
  const closeRef = useRef<HTMLButtonElement>(null);
  const titleIdRef = useRef<string>(`modal-title-${++modalIdCounter}`);

  useEffect(() => {
    if (!open || !closeOnEsc) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, closeOnEsc]);

  useEffect(() => {
    if (open) closeRef.current?.focus();
  }, [open]);

  // View-only: the body scrolls on a short screen (landscape phone 844×390,
  // bottom sheet), so a failure / result message inserted after an action
  // (a danger Banner, or an element marked data-reveal) could land outside
  // the visible part of the body while nothing on screen changed. Bring such
  // a message into view when it appears. Content present when the dialog
  // opens is left alone.
  const bodyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const body = bodyRef.current;
    if (!open || !body || typeof MutationObserver === 'undefined') return;
    const SELECTOR = '.banner.danger, [data-reveal]';
    const observer = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of Array.from(record.addedNodes)) {
          if (!(node instanceof HTMLElement)) continue;
          const target = node.matches(SELECTOR) ? node : node.querySelector<HTMLElement>(SELECTOR);
          if (target) {
            target.scrollIntoView?.({ block: 'nearest' });
            return;
          }
        }
      }
    });
    observer.observe(body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [open]);

  if (!open) return null;

  const labelId = title ? titleIdRef.current : labelledBy;
  const dialogStyle = width ? { width: `min(${width}px, calc(100vw - 32px))` } : undefined;

  return (
    <div
      className="scrim"
      onClick={closeOnScrimClick ? onClose : undefined}
      data-testid={testId ? `${testId}-scrim` : undefined}
    >
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelId}
        style={dialogStyle}
        onClick={(e) => e.stopPropagation()}
        data-testid={testId}
      >
        {title ? (
          <div className="dialog-header">
            <div id={titleIdRef.current} className="card-title">{title}</div>
            <button
              ref={closeRef}
              type="button"
              className="iconbtn"
              onClick={onClose}
              aria-label="Kapat"
            >
              <Icon.X />
            </button>
          </div>
        ) : null}
        <div className="dialog-body" ref={bodyRef}>{children}</div>
        {footer ? <div className="dialog-footer">{footer}</div> : null}
      </div>
    </div>
  );
}
